import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Empty,
  EmptyHeader,
  EmptyTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Spinner,
} from '@monksflow/monks-ui';
import { CheckCircle2, MoreVertical, Unplug } from 'lucide-react';
import {
  IQ_DATA_SOURCE_DESCRIPTIONS,
  IQ_DATA_SOURCE_LABELS,
  IQ_DATA_SOURCE_MARKS,
  IQ_DATA_SOURCE_QUERY_KEYS,
  iqDataSourcesQueryOptions,
  setDataSourceEnabled,
} from '@/entities/iq-data-source';
import { notificationService } from '@/lib/services/notification.service';
import { STATUS_BADGE_CLASSES } from '@/pages/MediaConnectors/components/AccountRow';
import type { DataSourcePaneProps } from './DataSourcePane.types';

/**
 * Connecting a source is a single row write, so it usually returns fast enough that the
 * busy state flashes and reads as "nothing happened". Holding it for a beat makes the
 * change legible.
 *
 * A floor rather than an added delay: a request slower than this is never padded, so the
 * cost is bounded to the fast path it exists for.
 */
const MIN_BUSY_MS = 1000;

const withMinimumDuration = async <T,>(work: Promise<T>, ms: number): Promise<T> => {
  const [result] = await Promise.all([work, new Promise((resolve) => setTimeout(resolve, ms))]);
  return result;
};

/**
 * Detail pane for a file-upload data source, standing in for `MediaConnectorPane` when the
 * rail's active id names one of those instead of an ad platform.
 *
 * A source the caller holds no licence for is absent from the list endpoint, and renders the
 * platform pane's unavailable state verbatim — wording it differently would tell a caller
 * that `yougov` is a recognised id, which is the leak that pane's guards exist to prevent.
 */
export function DataSourcePane({ source }: DataSourcePaneProps) {
  const { t } = useTranslation('knowledge');
  const queryClient = useQueryClient();
  const [confirmingDisconnect, setConfirmingDisconnect] = useState(false);

  const { data: connections, isPending } = useQuery(iqDataSourcesQueryOptions());
  const connection = (connections ?? []).find((item) => item.source === source);
  const name = IQ_DATA_SOURCE_LABELS[source];

  const mutation = useMutation({
    mutationFn: (next: boolean) =>
      withMinimumDuration(setDataSourceEnabled(source, next), MIN_BUSY_MS),
    onSuccess: () => {
      setConfirmingDisconnect(false);
      void queryClient.invalidateQueries({ queryKey: IQ_DATA_SOURCE_QUERY_KEYS.all });
    },
    onError: (_error, next) => {
      notificationService.showError(
        next
          ? t('dataSource.activateFailed', { name })
          : t('dataSource.deactivateFailed', { name }),
      );
    },
  });

  // Matches the platform pane: wait for the list before deciding, so the unavailable state
  // does not flash while it loads.
  if (isPending) {
    return (
      <div className="flex flex-1 items-center justify-center rounded-lg bg-card p-xl text-foreground-alt">
        <Spinner className="size-5" />
      </div>
    );
  }

  if (!connection) {
    return (
      <Empty>
        <EmptyHeader>
          <EmptyTitle>{t('mediaConnectors.mediaConnectorPane.platformUnavailable')}</EmptyTitle>
        </EmptyHeader>
      </Empty>
    );
  }

  return (
    <div
      data-testid="data-source-pane"
      className="flex min-h-0 flex-1 flex-col gap-xl rounded-lg bg-card p-xl"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span
            aria-hidden
            className="grid size-6 shrink-0 place-items-center rounded border border-border-light bg-surface text-xs font-medium text-foreground"
          >
            {IQ_DATA_SOURCE_MARKS[source]}
          </span>
          <h2 className="text-lg font-medium text-foreground">{name}</h2>
          {connection.enabled && (
            <Badge
              variant="ghost"
              roundness="full"
              className={`${STATUS_BADGE_CLASSES.success} px-2 py-0.5 text-xs font-medium`}
            >
              <CheckCircle2 className="mr-1 size-3" />
              {t('dataSource.connected')}
            </Badge>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-1">
          {mutation.isPending && (
            <Button variant="outline" size="sm" disabled>
              <Spinner className="mr-1.5" aria-hidden />
              {t('dataSource.activating')}
            </Button>
          )}

          {!connection.enabled && !mutation.isPending && (
            <Button variant="outline" size="sm" onClick={() => mutation.mutate(true)}>
              {t('dataSource.activate')}
            </Button>
          )}

          {connection.enabled && !mutation.isPending && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={t('dataSource.connectionOptions')}
                  className="size-8 p-0"
                >
                  <MoreVertical className="size-4 text-foreground-alt" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  variant="destructive"
                  onClick={() => setConfirmingDisconnect(true)}
                >
                  <Unplug className="mr-2 size-4" />
                  {t('dataSource.disconnect')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      <p className="max-w-prose text-sm text-foreground-alt">
        {IQ_DATA_SOURCE_DESCRIPTIONS[source]}
      </p>

      <p className="max-w-prose text-sm text-foreground-alt">
        {connection.enabled ? t('dataSource.enabledHint') : t('dataSource.disabledHint')}
      </p>

      <Dialog open={confirmingDisconnect} onOpenChange={setConfirmingDisconnect}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('dataSource.disconnectTitle', { name })}</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <p className="text-sm text-foreground-alt">
              {t('dataSource.disconnectDescription', { name })}
            </p>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmingDisconnect(false)}>
              {t('dataSource.cancel')}
            </Button>
            <Button
              variant="destructive"
              disabled={mutation.isPending}
              onClick={() => mutation.mutate(false)}
            >
              {mutation.isPending && <Spinner className="mr-1.5" aria-hidden />}
              {t('dataSource.disconnect')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

DataSourcePane.displayName = 'DataSourcePane';
